import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { BulleDeFeuille, BulleEnrichie, Feuille } from '../partage/api/types';
import { cles } from '../partage/relectures';
import { useFeuilles } from '../partage/useArbre';
import { arbreVisible, deplieesVisibles, type NoeudAPlacer } from '../hierarchie';
import { Bulle } from './Bulle';
import { ceQueDitLaLigne, groupesDeLaListe, rangerLesTaches } from './rangerLaListe';
import { RANGER, RANGEMENTS, type Rangement } from '../partage/vocabulaire';

/**
 * La feuille en liste — lecture et action : cocher, parcourir, retrouver. Refaite d'après sa
 * maquette (chantier `revue-design`, ticket 03, `design/liste.html`, décisions d'Igor du
 * 2026-10-03, « La Liste » 1 à 3) :
 *
 *   · **Ranger**, trois choix visibles : « Dans mon ordre » (par défaut), « Par importance »
 *     (les intertitres Urgent, Important, Quand tu veux, « Importance non réglée », puis ce qui
 *     n'est plus à faire), « Par date » (`rangerLaListe.ts`) ;
 *   · **les étapes se déplient sur place** par leur chevron : leurs tâches se posent dessous,
 *     en retrait. Le dépliage est celui de la Hiérarchie (`arbreVisible`, `deplieesVisibles`) :
 *     la feuille d'une étape dépliée se lit à la demande (`useFeuilles`), et une étape dépliée
 *     pas encore lue se montre repliée le temps de la lecture ;
 *   · **un clic sur une ligne ouvre sa fiche** en panneau à droite, la Liste reste visible et la
 *     ligne ouverte est surlignée ; un double-clic entre dedans (`Bulle.tsx`) ;
 *   · **au téléphone, un seul geste** (chantier `telephone-cadre`, ticket 04) : toucher une tâche
 *     ouvre sa fiche, toucher une étape y entre, l'appui long ouvre la fiche ; rien ne se déplie ;
 *   · à droite de chaque ligne, ce qu'elle dit d'elle-même (`ceQueDitLaLigne`) ; le « N sur M »
 *     d'une étape vient du serveur, avec la feuille (`decompte`).
 *
 * Ce que cette vue ne fait pas, et qu'il faut garder en tête : elle tolère l'empilement plat.
 * **Le format liste est ce qui ramène l'app vers la todo** (SPEC §10). C'est la carte qui fait
 * apparaître le regroupement sans un seul avertissement — d'où la bascule, toujours disponible.
 * Règle qui tient les vues ensemble : **rien ne doit être visible dans l'une et absent de
 * l'autre**, sinon la bascule devient un piège.
 */
export function VueListe({
  centreId,
  enfants,
  ordre,
  rangement,
  onRanger,
  ficheOuverte,
  hierarchisation,
  auPouce,
  onEntrer,
  onFiche,
  onRond,
  onDesigner,
}: {
  centreId: string;
  enfants: BulleDeFeuille[];
  /** « Mon ordre » de la feuille, tel que le serveur le rend (`Feuille.ordreHierarchique`). */
  ordre: readonly string[];
  rangement: Rangement;
  onRanger: (rangement: Rangement) => void;
  /** La bulle dont la fiche est ouverte : sa ligne est surlignée. */
  ficheOuverte: string | null;
  /** Les numéros posés pendant qu'on hiérarchise. `null` quand le mode est éteint. */
  hierarchisation: Map<string, number> | null;
  /** Au téléphone, le rond coche (ticket 05 du chantier telephone). */
  auPouce: boolean;
  onEntrer: (id: string) => void;
  /** Une tâche dépliée comprise : sa fiche trouve sa feuille dans ce qui est lu (`laFicheOuverte`). */
  onFiche: (id: string) => void;
  onRond: (bulle: BulleEnrichie) => void;
  onDesigner: (id: string) => void;
}) {
  const [depliees, setDepliees] = useState<ReadonlySet<string>>(() => new Set());
  const client = useQueryClient();

  /*
   * Le centre est toujours déplié : on est dedans, ses bulles sont le premier niveau. Au
   * téléphone, rien d'autre (chantier `telephone-cadre`, ticket 04) : on entre dans une étape,
   * un niveau à la fois, on ne la déplie pas sur place.
   */
  const ouvertes = new Set(auPouce ? [centreId] : [centreId, ...depliees]);
  const { arbre, visibles } = arbreVisible<BulleDeFeuille>(
    centreId,
    (id) => {
      if (id === centreId) return { enfants, ordre: enfants.map((e) => e.id) };
      const feuille = client.getQueryData<Feuille>(cles.feuille(id));
      return feuille
        ? {
            enfants: feuille.enfants,
            ordre: rangerLesTaches(feuille.enfants, rangement, feuille.ordreHierarchique).map((e) => e.id),
          }
        : undefined;
    },
    ouvertes
  );
  // Lire les feuilles des étapes dépliées : leurs tâches se posent dessous dès qu'elles arrivent.
  useFeuilles(deplieesVisibles(centreId, visibles, ouvertes));

  if (enfants.length === 0) return null;

  const noeuds = new Map<string, NoeudAPlacer>();
  const indexer = (n: NoeudAPlacer) => {
    noeuds.set(n.id, n);
    n.enfants.forEach(indexer);
  };
  indexer(arbre);

  const basculer = (id: string) =>
    setDepliees((avant) => {
      const apres = new Set(avant);
      if (apres.has(id)) apres.delete(id);
      else apres.add(id);
      return apres;
    });

  const ligne = (bulle: BulleDeFeuille, niveau: number) => (
    <Bulle
      key={bulle.id}
      bulle={bulle}
      niveau={niveau}
      dit={ceQueDitLaLigne(bulle, bulle.nbEnfants > 0 ? bulle.decompte : null)}
      deplie={ouvertes.has(bulle.id)}
      ouverte={ficheOuverte === bulle.id}
      rangEnAttente={hierarchisation?.get(bulle.id) ?? null}
      enHierarchisation={hierarchisation !== null}
      auPouce={auPouce}
      onEntrer={onEntrer}
      onFiche={onFiche}
      onRond={onRond}
      onDeplier={basculer}
      onDesigner={onDesigner}
    />
  );

  /** Les tâches dépliées sous une bulle, dans l'ordre de la Liste, chacune suivie des siennes. */
  const dessous = (id: string, niveau: number): ReactNode[] => {
    const noeud = noeuds.get(id);
    if (!noeud?.deplie || hierarchisation !== null) return [];
    return noeud.enfants.flatMap((n) => {
      const visible = visibles.get(n.id);
      if (!visible) return [];
      return [ligne(visible.bulle, niveau), ...dessous(n.id, niveau + 1)];
    });
  };

  return (
    <div className="vue-liste">
      <div className="liste-ranger" role="group" aria-label={RANGER}>
        <span className="liste-ranger-nom">{RANGER}</span>
        {RANGEMENTS.map(([cle, mot]) => (
          <button
            key={cle}
            type="button"
            className="liste-ranger-choix"
            aria-pressed={rangement === cle}
            onClick={() => onRanger(cle)}
          >
            {mot}
          </button>
        ))}
      </div>

      {groupesDeLaListe(enfants, rangement, ordre).map((groupe) => (
        <section key={groupe.cle} className="liste-groupe">
          {groupe.nom && (
            <h2 className="liste-intertitre">
              <b>{groupe.nom}</b>
              {/* La glose dit ce que l'intertitre VEUT DIRE. */}
              <span>{groupe.glose}</span>
            </h2>
          )}
          <ul>
            {groupe.bulles.flatMap((enfant) => [ligne(enfant, 1), ...dessous(enfant.id, 2)])}
          </ul>
        </section>
      ))}
    </div>
  );
}
